
import { useState } from "react";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import UserInfoForm from "@/components/pledge/UserInfoForm";
import PledgeHeader from "@/components/pledge/PledgeHeader";
import PledgeTierList from "@/components/pledge/PledgeTierList";
import PledgeFooter from "@/components/pledge/PledgeFooter";

interface PledgeTier {
  id: string;
  name: string;
  amount: number;
  regularPrice: number;
  description: string;
  features: string[];
  discount: string;
  savings: number;
}

interface PledgeSectionProps {
  id?: string;
}

interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

const PledgeSection = ({ id }: PledgeSectionProps) => {
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [showUserForm, setShowUserForm] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const pledgeTiers: PledgeTier[] = [
    {
      id: "early-supporter",
      name: "Early Supporter",
      amount: 35,
      regularPrice: 70,
      description: "2 months promotional access",
      discount: "Save 50%",
      savings: 35,
      features: [
        "2 months promotional access",
        "Then $35/month ongoing",
        "Early Supporter badge",
        "Standard gift package",
        "Monthly email updates"
      ]
    },
    {
      id: "movement-builder",
      name: "Movement Builder",
      amount: 90,
      regularPrice: 200,
      description: "6 months promotional access",
      discount: "Save 55%",
      savings: 110,
      features: [
        "6 months promotional access",
        "Then $200 every 6 months",
        "Movement Builder badge",
        "Standard gift package",
        "Quarterly virtual town halls"
      ]
    },
    {
      id: "revolution-partner",
      name: "Revolution Partner",
      amount: 140,
      regularPrice: 350,
      description: "12 months promotional access",
      discount: "Save 60%",
      savings: 210,
      features: [
        "12 months promotional access",
        "Then $350 every 12 months",
        "Revolution Partner badge",
        "Standard gift package",
        "Exclusive gifts",
        "Input on feature development"
      ]
    }
  ];

  const handlePledgeClick = (tierId: string) => {
    setSelectedTier(tierId);
    setShowUserForm(true);
  };

  const handleUserInfoSubmit = async (userInfo: UserInfo) => {
    if (!selectedTier) return;

    setLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-anonymous', {
        body: { 
          tier: selectedTier,
          userInfo
        }
      });

      if (error) throw error;
      
      // Open Stripe checkout in a new tab
      window.open(data.url, '_blank');
      toast.success("Redirecting to secure checkout...");
    } catch (error) {
      console.error("Error processing pledge:", error);
      toast.error("There was an error processing your pledge. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToPlans = () => {
    setShowUserForm(false);
    setSelectedTier(null);
  };

  if (showUserForm && selectedTier) {
    return (
      <section id={id} className="py-24 px-6 bg-secondary/5">
        <div className="max-w-4xl mx-auto">
          <UserInfoForm
            selectedTier={selectedTier}
            onSubmit={handleUserInfoSubmit}
            onBack={handleBackToPlans}
            loading={loading}
          />
        </div>
      </section>
    );
  }

  return (
    <section id={id} className="py-24 px-6 bg-secondary/5">
      <div className="max-w-5xl mx-auto">
        <PledgeHeader />
        <PledgeTierList
          tiers={pledgeTiers}
          selectedTier={selectedTier}
          loading={loading}
          onPledgeClick={handlePledgeClick}
        />
        <PledgeFooter />
      </div>
    </section>
  );
};

export default PledgeSection;
